/**
 * TypeScript mirror of the Rust IPC surface. Frozen in `CONTRACT.md` at the
 * repository root — change that file first, then both sides.
 *
 * Rust serialises everything as camelCase, so no field translation is needed.
 */

// ------------------------------------------------------------- constants ---

export const DWI_STAGES = [
  "prepdata",
  "gen_mask",
  "skull_strip",
  "registration",
  "inference",
  "report",
] as const;

export const PWI_STAGES = [
  "prepdata",
  "gen_mask",
  "skull_strip",
  "gen_ttp",
  "registration",
  "ttpadc_coreg",
  "inference",
  "report",
] as const;

export type DwiStage = (typeof DWI_STAGES)[number];
export type PwiStage = (typeof PWI_STAGES)[number];
export type StageId = DwiStage | PwiStage;

/**
 * Display titles, matching the banners the Python runners log. The DWI runner
 * numbers its banners `1/7` .. `5/6` .. `6/6`, so progress is derived from the
 * selected stage list and the banner is used only to name the current stage.
 */
export const DWI_STAGE_TITLES: Record<DwiStage, string> = {
  prepdata: "Preprocessing raw data",
  gen_mask: "Generating brain mask",
  skull_strip: "Performing skull stripping",
  registration: "Registration to MNI space",
  inference: "Running stroke segmentation",
  report: "Generating reports and visualizations",
};

export const PWI_STAGE_TITLES: Record<PwiStage, string> = {
  prepdata: "Preprocessing raw data",
  gen_mask: "Generating brain mask",
  skull_strip: "Performing skull stripping",
  gen_ttp: "Generating TTP maps",
  registration: "Registration to MNI space",
  ttpadc_coreg: "TTP-ADC coregistration",
  inference: "Running stroke segmentation",
  report: "Generating reports and visualizations",
};

export type Modality = "DWI" | "PWI";
export type PipelineKind = "dwi" | "pwi" | "combined";
export type StageState = "pending" | "running" | "completed" | "failed" | "skipped" | "cancelled";
export type JobState = "queued" | "running" | "completed" | "failed" | "cancelled";
export type Severity = "ok" | "warning" | "error";

// ---------------------------------------------------------------- system ---

export interface OpenAdsImage {
  reference: string;
  imageId: string;
  sizeBytes: number;
  createdIso: string;
  flavor: "gpu" | "cpu" | "unknown";
  /** The image's own architecture, e.g. "amd64". */
  architecture: string;
  /** True when that is not this machine's, so the run goes through emulation. */
  emulated: boolean;
}

export interface DockerStatus {
  available: boolean;
  version: string | null;
  daemonRunning: boolean;
  error: string | null;
  images: OpenAdsImage[];
  selectedImage: string | null;
  gpuRuntimeAvailable: boolean;
}

export interface GpuDevice {
  index: number;
  name: string;
  memoryTotalMb: number;
  memoryUsedMb: number;
  utilizationPct: number | null;
}

export interface GpuStatus {
  available: boolean;
  devices: GpuDevice[];
  driverVersion: string | null;
  cudaVersion: string | null;
  error: string | null;
}

export interface StorageInfo {
  path: string;
  freeBytes: number;
  totalBytes: number;
}

export interface SystemStatus {
  /** What a run would actually use. `docker` below is one of its two halves. */
  engine: EngineStatus;
  docker: DockerStatus;
  gpu: GpuStatus;
  storage: StorageInfo[];
  appVersion: string;
  platform: "linux" | "windows" | "macos";
  /** How many subjects may run at once, and why that number. */
  concurrency: ConcurrencyStatus;
}

/**
 * What the machine can hold. The binding constraint is memory, not cores: one
 * run peaks at 8.4 GB on Metal and 21 GB on the CPU, both measured on this
 * hardware, so a 24 GB machine runs two subjects at once with Metal on and one
 * without.
 */
export interface ConcurrencyStatus {
  /** Runs allowed at once, after any preference. */
  limit: number;
  /** What the hardware alone allows; differs from `limit` only if overridden. */
  automatic: number;
  /** Threads each run gives its numeric libraries. */
  threadsPerJob: number;
  /** One sentence naming the binding constraint. */
  reason: string;
}

// ---------------------------------------------------------------- engine ---

export type UiMode = "clinical" | "research";

export type EngineKind = "native" | "docker";
export type EngineSelection = "auto" | "native" | "docker";
/** `emulated` = an amd64 image on an arm64 host: correct, and slow. */
export type DeviceClass = "cuda" | "cpu" | "emulated";

export interface PythonCandidate {
  path: string;
  version: string | null;
  origin: "bundled" | "preference" | "env" | "managed-venv" | "checkout-venv" | "path";
  usable: boolean;
  note: string;
}

export interface CheckoutCandidate {
  path: string;
  origin: "bundled" | "preference" | "env" | "managed" | "well-known";
  hasRunners: boolean;
  hasConfigs: boolean;
  hasModels: boolean;
  hasAtlases: boolean;
  missing: string[];
}

export interface NativeEngineProbe {
  ok: boolean;
  pythonPath: string | null;
  pythonVersion: string | null;
  checkoutPath: string | null;
  adsImportable: boolean;
  packages: Record<string, string>;
  missing: string[];
  cudaAvailable: boolean;
  /**
   * Reported honestly and never acted on: the `ads` model code selects CUDA or
   * CPU and has no MPS branch, so Apple Silicon runs on CPU.
   */
  mpsAvailable: boolean;
  modelsDir: string | null;
  atlasesDir: string | null;
  cpuThreads: number;
  error: string | null;
  /** Ordered, specific things the user can do about `error`. */
  remediation: string[];
}

export interface EngineStatus {
  kind: EngineKind;
  selection: EngineSelection;
  ready: boolean;
  deviceClass: DeviceClass;
  deviceLabel: string;
  error: string | null;
  remediation: string[];
  native: NativeEngineProbe | null;
  docker: DockerStatus;
}

export interface EngineSetupStep {
  id: string;
  title: string;
  state: StageState;
  durationSec: number | null;
  command: string;
}

export interface EngineSetupStatus {
  setupId: string;
  state: JobState;
  steps: EngineSetupStep[];
  logPath: string;
  errorMessage: string | null;
}

export interface EngineSetupRequest {
  /** "use-checkout" validates what is there; "create-venv" also builds one. */
  mode: "use-checkout" | "create-venv";
  checkoutPath: string;
  hostPythonPath?: string | null;
}

export const EVT_ENGINE_SETUP_LOG = "engine://setup/log";
export const EVT_ENGINE_SETUP_STEP = "engine://setup/step";
export const EVT_ENGINE_SETUP_STATE = "engine://setup/state";

// ------------------------------------------------------------ input scan ---

export interface DetectedFile {
  path: string;
  fileName: string;
  relPath: string;
  fileType: "NIfTI" | "JSON" | "Other";
  modality: "DWI" | "ADC" | "b0" | "PWI" | "TTP" | "Mask" | "Stroke" | "HP" | "Unknown";
  sizeBytes: number;
  dimensions: number[] | null;
  voxelSizeMm: number[] | null;
  datatype: string | null;
  status: Severity;
  detail: string;
}

export interface Readiness {
  ready: boolean;
  missing: string[];
  notes: string[];
}

export interface SubjectScan {
  subjectDir: string;
  subjectId: string;
  baseId: string;
  files: DetectedFile[];
  validCount: number;
  warningCount: number;
  errorCount: number;
  /** The Python runners hard-exit on a dataset root, so the UI must block Run. */
  isDatasetRoot: boolean;
  readiness: { dwi: Readiness; pwi: Readiness; combined: Readiness };
}

export interface DatasetSubjectRef {
  dir: string;
  subjectId: string;
  fileCount: number;
  files: string[];
}

export interface DatasetScan {
  subjects: DatasetSubjectRef[];
}

export interface NiftiHeaderInfo {
  path: string;
  dim: number[];
  pixdim: number[];
  datatype: string;
  orientation: string;
  sformCode: number;
  qformCode: number;
  scaleSlope: number;
  scaleInter: number;
  isCompressed: boolean;
  affine?: number[][];
  volumeCount?: number;
  voxelVolumeMm3?: number | null;
}

// ------------------------------------------------------------------ case ---

export interface StageSelection {
  dwi: DwiStage[];
  pwi: PwiStage[];
}

export interface CaseNode {
  id: string;
  label: string;
  relPath: string;
  origin: "input" | "output";
  space: "native" | "MNI152" | "DWI" | "unknown";
  kind: "image" | "mask" | "transform" | "atlas" | "table" | "report" | "figure";
  derived: boolean;
  meta: Record<string, string | number | null>;
}

export interface CaseManifest {
  schemaVersion: 1;
  caseId: string;
  subjectId: string;
  studyName: string;
  pipelineKind: PipelineKind;
  createdIso: string;
  modifiedIso: string;
  inputRoot: string;
  subjectRelPath: string;
  pwiSubjectRelPath: string | null;
  outputRoot: string;
  gpuIndex: string;
  dockerImage: string | null;
  stages: StageSelection;
  cleanupEnabled: boolean;
  notes: string;
  images: CaseNode[];
  masks: CaseNode[];
  transforms: CaseNode[];
  atlases: CaseNode[];
  measurements: CaseNode[];
  reports: CaseNode[];
}

export interface CaseDraft {
  subjectDir: string;
  outputRoot: string;
  subjectId: string;
  studyName: string;
  pipelineKind: PipelineKind;
  pwiSubjectDir?: string | null;
  gpuIndex: string;
  dockerImage?: string | null;
  stages: StageSelection;
  cleanupEnabled: boolean;
}

export interface RecentCase {
  caseId: string;
  subjectId: string;
  studyName: string;
  caseJsonPath: string;
  pipelineKind: PipelineKind;
  modality: string;
  lastModifiedIso: string;
  status: JobState | "not_created";
  exists: boolean;
}

// ------------------------------------------------------------------- job ---

export interface JobSpec {
  caseId: string;
  /** Force one engine for this run. Omit to follow the saved preference. */
  engine?: EngineSelection | null;
  pipelineKind: PipelineKind;
  stages: StageSelection;
  gpuIndex: string;
  dockerImage: string | null;
  cleanupEnabled: boolean;
  /**
   * Pipeline parameters the reader changed, as dotted paths into the config the
   * runner loads — `{"data_raw.bvalue": 900}`. Only keys the runner actually
   * reads are offered; `src/pipeline/params.ts` says which those are. Applied by
   * a shim in the managed environment, so a Docker run refuses them rather than
   * running with the pipeline's own values.
   */
  configOverrides?: Record<string, ParamValue>;
}

/** What a pipeline parameter can be set to. */
export type ParamValue = string | number | boolean | number[];

export interface JobHandle {
  jobId: string;
  command: string[];
  logPath: string;
  /** Echoed back so the optimistic JobStatus the session builds is truthful. */
  engine: EngineKind;
  deviceLabel: string;
  deviceClass: DeviceClass;
}

export interface StageStatus {
  id: StageId;
  modality: Modality;
  title: string;
  state: StageState;
  startedIso: string | null;
  finishedIso: string | null;
  durationSec: number | null;
}

export interface JobStatus {
  jobId: string;
  caseId: string;
  /**
   * Which subject this run is for. On the job rather than looked up from the
   * open case, because a parallel batch has several runs and only one of them is
   * the case on screen.
   */
  subjectId: string;
  state: JobState;
  containerId: string | null;
  startedIso: string | null;
  finishedIso: string | null;
  exitCode: number | null;
  progressPct: number;
  currentStage: StageId | null;
  currentModality: Modality | null;
  stages: StageStatus[];
  errorMessage: string | null;
  /**
   * Kept on the finished job, so the Jobs table stays truthful afterwards and
   * the progress weighting scores it against the device it actually used.
   */
  engine: EngineKind;
  deviceLabel: string;
  deviceClass: DeviceClass;
  /** See `StageHealth`. Cleared the moment the run speaks again. */
  health: StageHealth | null;
}

export interface CommandPreview {
  argv: string[];
  display: string;
  engine: EngineKind;
  /** Variables the argv needs; a native command line is not reproducible
   *  without PYTHONPATH. */
  env: [string, string][];
}

// ---------------------------------------------------------------- events ---

export const EVT_JOB_LOG = "job://log";
export const EVT_JOB_STAGE = "job://stage";
export const EVT_JOB_PROGRESS = "job://progress";
export const EVT_JOB_STATE = "job://state";

/**
 * `trace` covers the ANTs `DIAGNOSTIC,` flood and parameter dumps: a full DWI
 * run emits 865 lines of which only 53 are structured, so these are tagged
 * separately and collapsed by default rather than evicting real output from
 * the log buffer.
 */
export type LogLevel = "trace" | "info" | "warn" | "error";

export interface JobLogEntry {
  seq: number;
  ts: string;
  line: string;
  level: LogLevel;
}

/** Batched: the backend flushes every ~100ms or ~50 lines, not per line. */
export interface JobLogEvent {
  jobId: string;
  entries: JobLogEntry[];
}

export interface JobStageEvent {
  jobId: string;
  stage: StageId;
  modality: Modality;
  state: StageState;
}

/**
 * What the process tree was doing at one instant, sampled only while a stage
 * is producing no output. Everything here is measured; a field the backend
 * could not read comes back null rather than as a plausible number.
 */
export interface StageHealth {
  /** Seconds since the run last printed anything. */
  quietSeconds: number;
  /** Summed over the process tree, in units of one core: 613.5 is six busy. */
  cpuPercent: number | null;
  residentMb: number | null;
  totalMemoryMb: number | null;
  swapFreeMb: number | null;
  /**
   * A large resident set with idle cores: waiting on memory, not computing.
   * Only ever true when both halves of that signature were actually read.
   */
  paging: boolean;
  /** One sentence for the status line, written from the fields above. */
  note: string;
}

export interface JobProgressEvent {
  jobId: string;
  progressPct: number;
  currentStage: StageId | null;
  /** Present only on the samples taken during a silent stage. */
  health: StageHealth | null;
}

export interface JobStateEvent {
  jobId: string;
  state: JobState;
  exitCode: number | null;
  errorMessage: string | null;
}

// --------------------------------------------------------------- outputs ---

export interface OutputNode {
  name: string;
  relPath: string;
  isDir: boolean;
  sizeBytes: number;
  modifiedIso: string;
  kind: "nifti" | "csv" | "json" | "png" | "pdf" | "txt" | "mat" | "other";
  children?: OutputNode[];
}

// --------------------------------------------------------------- results ---

export interface SegmentationMetrics {
  dice: number | null;
  precision: number | null;
  sensitivity: number | null;
  sdr: number | null;
  predVolumeVox: number | null;
  trueVolumeVox: number | null;
}

export interface MetricsFile {
  mni: SegmentationMetrics | null;
  orig: SegmentationMetrics | null;
  mniPostproc: SegmentationMetrics | null;
  origPostproc: SegmentationMetrics | null;
}

export interface QfvTable {
  atlas: string;
  subjectId: string;
  /**
   * The CSV's `volml` column, which holds `log10(volume in mL)` — see
   * `pipe_CalQFV.py`: `np.log10((np.sum(stroke_img) + 1) / 1000)`. Pass it
   * through `qfvLogVolumeToMl()` before showing it to anyone. Named for what
   * it is; the old `volMl` read as millilitres and is not.
   */
  volLog10Ml: number;
  rois: { name: string; qfv: number }[];
}

export interface LesionLoadTable {
  atlas: string;
  entries: { name: string; voxels: number }[];
}

export interface RegionalRow {
  territory: string;
  coreVolumeMl: number | null;
  corePct: number | null;
  hypoVolumeMl: number | null;
  hypoPct: number | null;
}

export interface ResultsSummary {
  caseId: string;
  subjectId: string;
  pipelineKind: PipelineKind;
  /**
   * Headline volumes as the radiological report quotes them. The pipeline
   * reports in MNI152 space, where a voxel is 1 mm, so these are NOT the
   * volumes on the patient's own grid — see the `*NativeMl` fields.
   */
  coreVolumeMl: number | null;
  hypoperfusionVolumeMl: number | null;
  mismatchVolumeMl: number | null;
  mismatchRatio: number | null;
  /** Which space the four fields above are in: "MNI152" or "native". */
  coreVolumeSpace: string;
  /**
   * The same lesions measured on the acquired grid. Registration to MNI152
   * inflates a typical DWI lesion by roughly half (141.64 mL against 91.85 mL
   * on the reference subject), which is the difference between two sides of a
   * volume threshold — so show both rather than picking one.
   */
  coreVolumeNativeMl: number | null;
  hypoperfusionVolumeNativeMl: number | null;
  mismatchVolumeNativeMl: number | null;
  mismatchRatioNative: number | null;
  aspects: number | null;
  processingSeconds: number | null;
  dwiMetrics: MetricsFile | null;
  pwiMetrics: MetricsFile | null;
  regional: RegionalRow[];
  qfvTables: QfvTable[];
  lesionLoadTables: LesionLoadTable[];
  figures: { label: string; path: string }[];
  reports: { label: string; path: string; kind: "txt" | "pdf" }[];
  radiologicalReportText: string | null;
  warnings: string[];
}

// ----------------------------------------------------------- preferences ---

export interface Preferences {
  defaultInputRoot: string | null;
  defaultOutputRoot: string | null;
  defaultGpuIndex: string;
  defaultDockerImage: string | null;
  cleanupEnabled: boolean;
  theme: "light" | "dark" | "system";
  linkViews: boolean;
  overlayOpacity: number;
  overlayColormap: string;
  showLabels: boolean;
  confirmBeforeCancel: boolean;
  /**
   * Who the app is dressed for. "clinical" hides the research utilities and
   * leads with the stroke read; "research" shows everything.
   */
  uiMode: UiMode;
  /** Which engine to run on. "auto" prefers whichever is usable. */
  preferredEngine: EngineSelection;
  /** An OpenADS checkout: the `ads` package, configs, weights and atlases. */
  engineCheckoutPath: string | null;
  /** A Python interpreter with the dependencies installed. */
  enginePythonPath: string | null;
  /**
   * Run the torch stages on Apple's GPU. Absent means "decide from the
   * hardware", which is on wherever Metal exists.
   */
  metalAcceleration?: boolean | null;
  /**
   * How many subjects of a batch may run at once. Absent means "decide from the
   * hardware". An explicit number is obeyed even above what memory suggests,
   * and the run form then says what that will cost.
   */
  maxParallelJobs?: number | null;
}

// -------------------------------------------------------- volume tools ---

export interface VolumeToolRequest {
  caseId: string;
  op: string;
  inputPath: string;
  referencePath?: string | null;
  params?: Record<string, unknown>;
}

export interface VolumeToolResult {
  outputPath: string | null;
  kind: string;
  stats: Record<string, unknown>;
  message: string;
}

// ------------------------------------------------------------- cohort ---
// Reviewing a folder of subjects rather than opening them one at a time.
// Mirrors `src-tauri/src/cohort.rs`; see that file for why the cohort's own
// majority, and not a table of correct values, is the reference.

/** A geometry reduced to what has to match for two images to be processed together. */
export interface GeometrySignature {
  /** Spatial extent only; a 4D series' volume count is reported separately. */
  dim: number[];
  /** Hundredths of a millimetre, rounded, so 1.1979 and 1.198 are one geometry. */
  voxelUm: number[];
  datatype: string;
  orientation: string;
}

/** One thing wrong with one subject, already phrased for a reader. */
export interface CohortIssue {
  /** "missing" | "cohort" | "internal" | "file" */
  kind: string;
  severity: Severity;
  role: string | null;
  message: string;
}

export interface CohortImage {
  role: string;
  fileName: string;
  sizeBytes: number;
  signature: GeometrySignature | null;
  /** Time points for a 4D series; null for a 3D image. */
  volumes: number | null;
}

export interface CohortSubject {
  subjectId: string;
  dir: string;
  fileCount: number;
  sizeBytes: number;
  roles: string[];
  images: CohortImage[];
  readiness: { dwi: Readiness; pwi: Readiness; combined: Readiness };
  issues: CohortIssue[];
  severity: Severity;
  /**
   * Has what a run needs AND nothing that would stop it. Presence alone is not
   * enough: a subject with every file and a grid mismatch will still fail.
   */
  dwiReady: boolean;
  pwiReady: boolean;
}

export interface GeometryVariant {
  signature: GeometrySignature;
  /** `192×192×38 · 1.20×1.20×4.00 mm · LAS+ · int16` */
  description: string;
  subjectIds: string[];
}

export interface ConsistencyGroup {
  role: string;
  present: number;
  /** The geometry a majority share. Null when the cohort splits — then there
   *  is nothing to call its own, and `variants` carries the groups instead. */
  common: GeometrySignature | null;
  agreeing: number;
  /** Every distinct geometry seen, commonest first. */
  variants: GeometryVariant[];
}

export interface RoleCoverage {
  role: string;
  present: number;
  missingSubjectIds: string[];
}

export type FolderKind =
  | "datasetRoot"
  | "subject"
  | "outputTree"
  | "unrecognised"
  | "unreadable";

/** What kind of folder this is, and what the verdict rests on. */
export interface FolderVerdict {
  kind: FolderKind;
  evidence: string[];
  subjectDirs: string[];
  suggestion: string | null;
}

/** A family of names that share a shape. */
export interface NamePattern {
  /** `sub-{8 hex}` or `{id}_DWI.nii.gz` */
  pattern: string;
  count: number;
  examples: string[];
  members: string[];
}

/** A file that exists, plainly means a role, and will not be found. */
export interface Unreachable {
  subjectId: string;
  role: string;
  fileName: string;
  /** What it would have to be called. */
  expected: string;
}

export interface NamingReport {
  subjectPatterns: NamePattern[];
  filePatterns: NamePattern[];
  unreachable: Unreachable[];
}

export interface DistributionPoint {
  subjectId: string;
  value: number;
}

/**
 * A quantity across the cohort. Reported as a spread with the outliers named,
 * not as a mean: the useful statement is "sub-0117 has three files and
 * everyone else has eight".
 */
export interface Distribution {
  label: string;
  /** "files" | "bytes" */
  unit: string;
  count: number;
  min: number;
  q1: number;
  median: number;
  q3: number;
  max: number;
  points: DistributionPoint[];
  outliers: DistributionPoint[];
}

export interface CohortScan {
  root: string;
  scannedIso: string;
  verdict: FolderVerdict;
  naming: NamingReport;
  subjects: CohortSubject[];
  coverage: RoleCoverage[];
  consistency: ConsistencyGroup[];
  subjectCount: number;
  dwiReadyCount: number;
  pwiReadyCount: number;
  withErrors: number;
  withWarnings: number;
  totalBytes: number;
  /** Subject folders that could not be read at all, with the reason. */
  unreadable: CohortIssue[];
  distributions: Distribution[];
}

/** Roles a file can be assigned when the detector gets it wrong. */
export const STAGEABLE_ROLES = ["DWI", "ADC", "b0", "PWI", "stroke"] as const;
export type StageableRole = (typeof STAGEABLE_ROLES)[number];

export interface RoleChoice {
  role: StageableRole;
  /** The file as it sits in the reader's own folder. */
  path: string;
}

export interface PreparedFile {
  role: string;
  source: string;
  fileName: string;
  /** "linked" or "copied". */
  method: string;
  sizeBytes: number;
}

/**
 * A subject folder built so the pipeline's own name-based discovery reads the
 * files the reader picked. Hard links where the filesystem allows it, so the
 * original folder is untouched and nothing is duplicated on disk.
 */
export interface PreparedInput {
  dir: string;
  files: PreparedFile[];
  copiedAny: boolean;
}

// ------------------------------------------------------------ projects ---
// A project is a question, a cohort, a protocol and a record of what was done.
// Mirrors `src-tauri/src/projects.rs`; see PROJECTS.md for why a workspace
// stopped meaning "the layout I had open".

export interface Exclusion {
  subjectId: string;
  /** Whoever's words. Shown verbatim, never summarised. */
  reason: string;
  /** True when the reason came from the data scan rather than a person. */
  fromScan: boolean;
  iso: string;
}

export interface Cohort {
  root: string;
  scannedIso: string;
  /** Null when only the fast inventory has been run. */
  integrityCheckedIso: string | null;
  included: string[];
  excluded: Exclusion[];
}

/** What every subject in this cohort is meant to receive. */
export interface Protocol {
  pipelineKind: PipelineKind;
  stages: StageSelection;
  fixedIso: string;
}

export type RunState = "completed" | "failed" | "cancelled";

export interface ProjectRun {
  subjectId: string;
  jobId: string;
  state: RunState;
  /** What the run actually did, which is what makes a deviation visible. */
  stages: StageSelection;
  pipelineKind: PipelineKind;
  startedIso: string;
  finishedIso: string | null;
  seconds: number | null;
  deviceLabel: string;
  error: string | null;
}

export interface ProjectLogEntry {
  iso: string;
  /** "run" | "excluded" | "protocol" | "note" | "cohort" */
  kind: string;
  text: string;
}

export interface Project {
  id: string;
  name: string;
  createdIso: string;
  modifiedIso: string;
  /** The researcher's own words. Empty is allowed; invented is not. */
  question: string;
  cohort: Cohort;
  protocol: Protocol;
  outputRoot: string;
  runs: ProjectRun[];
  log: ProjectLogEntry[];
}

/**
 * Where one subject stands, derived from the runs rather than stored.
 * `doneOffProtocol` exists so a subject processed differently is never counted
 * among the ones processed the same.
 */
export type SubjectProgress =
  | "notStarted"
  | "done"
  | "doneOffProtocol"
  | "failed"
  | "cancelled";

export interface ProjectSummary {
  id: string;
  name: string;
  question: string;
  modifiedIso: string;
  pipelineKind: PipelineKind;
  included: number;
  excluded: number;
  completed: number;
  failed: number;
  notStarted: number;
  offProtocol: number;
  outputRoot: string;
  /** False when the cohort folder is no longer where it was. */
  cohortExists: boolean;
}

// -------------------------------------------------- integrity check ------
// Opening every file: the faults only visible from inside. Minutes, not
// seconds, so it streams and can be stopped. Mirrors `scan/integrity.rs`.

export const EVT_INTEGRITY = "scan://integrity";

export interface IntegrityProblem {
  /** "truncated" | "size" | "empty" | "constant" | "nan" | "unreadable" */
  kind: string;
  /** "error" | "warning" */
  severity: string;
  /** What it means, not what it is. */
  message: string;
}

export interface FileIntegrity {
  path: string;
  fileName: string;
  dataBytes: number;
  declaredBytes: number | null;
  min: number | null;
  max: number | null;
  nonzero: number;
  nan: number;
  infinite: number;
  problems: IntegrityProblem[];
}

export interface IntegrityProgress {
  done: number;
  total: number;
  current: string;
  /** Only files with something wrong; a clean file needs no event. */
  findings: FileIntegrity[];
  /**
   * On the last event: the intensity range of every file that read, by role.
   * Built with the same outlier rule the inventory uses.
   */
  intensity: Distribution[];
  finished: boolean;
  cancelled: boolean;
}
